/**
 * @file lineUnits.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description A LINE's unit weight, read the one way every screen and the
 *              server agree on — including "None" (Cam, 2026-10-12): a line
 *              that carries no weight at all, for a service charge, a
 *              weighbridge charge or anything else that does not travel.
 *
 *   HOW "NONE" IS STORED. A line's own unit weight has always meant "blank =
 *   the item's": orders.order_lines.unit_weight_kg NULL, a PO line's
 *   unitWeightKg null, the grid's unit_weight_kg ''. So None cannot be blank
 *   — a blank would quietly fall back to the item's weight. It is stored as
 *   **0** (unit_weight_kg = 0 on the order line, unitWeightKg: 0 on the PO
 *   line, '0' in the grid), with pallets 0 and no qty per pallet. Nothing
 *   stored a 0 before (checked on production 2026-10-12: no order line, PO
 *   line or item had one), and the SQL readers' COALESCE(line, item) already
 *   take a 0 as set — so a None line weighs 0 T on the boards, in the loads
 *   and in the driver's figures, with no change there. The JS readers that
 *   treated 0 as "missing" read it through these helpers instead.
 *
 *   PALLETS ONLY FOR PACKS. A line counted in packs — a known unit weight
 *   that is not a tonne (a bag, a tote, an "Other" pack) — is booked with an
 *   outside haulier by the pallet, so a Third-Party Delivery order needs its
 *   pallets. A tonne line, a None line and a line whose weight nobody knows
 *   need none (Cam, 2026-10-12: "only if bagged").
 *
 *   The server's utils/lineUnits.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it. Its import
 *   of palletsFor resolves to the server's own utils/units.js, which keeps
 *   the same rule.
 */

import { palletsFor } from './units';

/** The Units dropdown's value for "no weight". */
export const NO_WEIGHT_UNIT = 'none';

/** The Units dropdown's wording for it. */
export const NO_WEIGHT_LABEL = 'None — no weight';

const blank = (v) => v === '' || v === null || v === undefined;

/** A line's own unit weight saying "None": set, and zero. Blank is NOT None. */
export function isNoWeight(weight) {
  if (blank(weight)) return false;
  const n = Number(weight);
  return Number.isFinite(n) && n === 0;
}

const positive = (v) => {
  if (blank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The weight one unit of the line counts as, kg: 0 for a None line, the
 * line's own weight where set, else the item's, else null (unknown).
 */
export function lineWeightKg(lineWeight, itemWeight = null) {
  if (isNoWeight(lineWeight)) return 0;
  return positive(lineWeight) ?? positive(itemWeight);
}

/** Counted in packs: a known unit weight that is not a tonne. */
export function isPackWeight(kg) {
  const n = positive(kg);
  return n !== null && n !== 1000;
}

/**
 * Does this line need pallets on a Third-Party Delivery order? Only when it
 * is counted in packs (its own weight, else its item's).
 * @param {{ unit_weight_kg? }} line
 * @param {{ unit_weight_kg? }|null} item
 */
export function needsPallets(line, item = null) {
  return isPackWeight(lineWeightKg(line?.unit_weight_kg, item?.unit_weight_kg));
}

/**
 * The lines of a Third-Party Delivery order that need pallets and have none:
 * packed lines whose pallets are neither typed nor worked out from a qty per
 * pallet (the line's, else the item's). `itemOf(line)` gives the line's
 * inventory item ({ unit_weight_kg, qty_per_pallet }) or null.
 */
export function linesMissingPallets(lines, itemOf = () => null) {
  return (Array.isArray(lines) ? lines : []).filter((line) => {
    const item = itemOf(line) || null;
    if (!needsPallets(line, item)) return false;
    const perPallet = positive(line?.qty_per_pallet) ?? positive(item?.qty_per_pallet);
    return !(palletsFor(line?.quantity, perPallet, line?.pallets) > 0);
  });
}

/** What choosing None sets on a grid line: no weight, no pallets. */
export function noWeightUnits() {
  return { unit_weight_kg: '0', qty_per_pallet: '', pallets: '0', unit_is_other: false, pallet_weight_kg: '' };
}
